import React, { useEffect, useRef, useState } from "react";
import { Music2, Volume2, VolumeX } from "lucide-react";

const STORAGE_KEY = "gentleflow_ambient_music";

const AmbientMusicToggle = () => {
  const [enabled, setEnabled] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "on"; } catch { return false; }
  });
  const audioRef = useRef(null);

  const stop = () => {
    const audio = audioRef.current;
    if (!audio) return;
    try { audio.nodes.forEach(node => { try { node.stop(); } catch {} }); } catch {}
    try { audio.context.close(); } catch {}
    audioRef.current = null;
  };

  const start = async () => {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return false;
    const context = new AudioContext();
    if (context.state === "suspended") await context.resume();

    const master = context.createGain();
    master.gain.value = 0.018;
    master.connect(context.destination);

    const notes = [261.63, 329.63, 392.0, 329.63, 293.66, 349.23, 440.0, 349.23];
    const nodes = [];
    notes.forEach((frequency, index) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = "sine";
      oscillator.frequency.value = frequency;
      const at = context.currentTime + index * 1.6;
      gain.gain.setValueAtTime(0, at);
      gain.gain.linearRampToValueAtTime(0.65, at + 0.35);
      gain.gain.linearRampToValueAtTime(0, at + 1.45);
      oscillator.connect(gain);
      gain.connect(master);
      oscillator.start(at);
      oscillator.stop(at + 1.5);
      nodes.push(oscillator);
    });

    audioRef.current = { context, nodes };
    return true;
  };

  const toggle = async () => {
    if (enabled) {
      stop();
      setEnabled(false);
      localStorage.setItem(STORAGE_KEY, "off");
      return;
    }
    const started = await start();
    if (!started) return;
    setEnabled(true);
    localStorage.setItem(STORAGE_KEY, "on");
  };

  useEffect(() => () => stop(), []);

  return (
    <button
      type="button"
      onClick={toggle}
      className="fixed bottom-6 left-6 z-40 inline-flex items-center gap-2 rounded-full border border-[#E8E3DB] bg-white/90 px-4 py-2.5 text-sm font-medium text-[#4A2937] shadow-lg backdrop-blur-md transition-all hover:-translate-y-0.5 hover:shadow-xl"
      aria-label={enabled ? "Turn off soft ambient music" : "Turn on soft ambient music"}
      title={enabled ? "Soft Ambient Music: On" : "Soft Ambient Music: Off"}
      data-testid="ambient-music-toggle"
    >
      {enabled ? <Volume2 className="h-4 w-4 text-[#C084A0]" /> : <VolumeX className="h-4 w-4 text-[#8A7A85]" />}
      <Music2 className="h-4 w-4" />
      <span className="hidden sm:inline">Ambient</span>
    </button>
  );
};

export default AmbientMusicToggle;
